<template>
  <div class="container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="main-title">高考志愿填报模拟系统</h1>
      <p class="subtitle">模拟真实填报流程，帮助您熟悉操作，避免正式填报时出错</p>
    </div>

    <!-- 进度条 -->
    <div class="progress-section">
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
      <div class="progress-steps">
        <div 
          v-for="(step, index) in progressSteps" 
          :key="index"
          class="step-item"
          :class="{ active: currentStep >= index, completed: currentStep > index }"
        >
          <span class="step-number">{{ index + 1 }}</span>
          <span class="step-text">{{ step }}</span>
        </div>
      </div>
    </div>

    <!-- 步骤1：考生信息 -->
    <div v-if="currentStep === 0" class="form-section">
      <div class="section-header">
        <h2 class="section-title">第一步：填写考生信息</h2>
        <p class="section-desc">请准确填写您的个人信息</p>
      </div>
      
      <el-form :model="form" label-width="120px" class="info-form" :rules="rules">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="考生姓名" prop="name">
              <el-input 
                v-model="form.name" 
                placeholder="请输入姓名"
                :disabled="submitted"
                :maxlength="20"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="考生号" prop="examineeNumber">
              <el-input 
                v-model="form.examineeNumber" 
                placeholder="请输入考生号（14位）"
                :disabled="submitted"
                :maxlength="14"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="所在省份" prop="province">
              <el-select 
                v-model="form.province" 
                placeholder="请选择省份"
                :disabled="submitted"
              >
                <el-option
                  v-for="province in provinces"
                  :key="province.value"
                  :label="province.label"
                  :value="province.value"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="科类" prop="subjectType">
              <el-select 
                v-model="form.subjectType" 
                placeholder="请选择科类"
                :disabled="submitted"
              >
                <el-option label="理科" value="science"></el-option>
                <el-option label="文科" value="arts"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <div class="step-actions">
        <el-button type="primary" @click="nextStep" :disabled="!validateStep1">
          下一步：填写成绩信息
        </el-button>
      </div>
    </div>

    <!-- 步骤2：成绩信息 -->
    <div v-if="currentStep === 1" class="form-section">
      <div class="section-header">
        <h2 class="section-title">第二步：填写成绩信息</h2>
        <p class="section-desc">请准确填写您的高考成绩和排名信息</p>
      </div>
      
      <el-form :model="form" label-width="120px" class="info-form" :rules="rules">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="高考总分" prop="totalScore">
              <el-input-number
                v-model="form.totalScore"
                :min="0"
                :max="750"
                controls-position="right"
                :disabled="submitted"
              ></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="省内排名" prop="ranking">
              <el-input-number
                v-model="form.ranking"
                :min="1"
                :max="1000000"
                controls-position="right"
                :disabled="submitted"
              ></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="批次" prop="batch">
              <el-select 
                v-model="form.batch" 
                placeholder="请选择批次"
                :disabled="submitted"
              >
                <el-option label="本科提前批" value="early"></el-option>
                <el-option label="本科第一批" value="first"></el-option>
                <el-option label="本科第二批" value="second"></el-option>
                <el-option label="专科批" value="specialty"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <div class="step-actions">
        <el-button @click="prevStep">上一步</el-button>
        <el-button type="primary" @click="nextStep" :disabled="!validateStep2">
          下一步：填报志愿
        </el-button>
      </div>
    </div>

    <!-- 步骤3：志愿填报 -->
    <div v-if="currentStep === 2" class="form-section">
      <div class="section-header">
        <h2 class="section-title">第三步：填报志愿（最多6个）</h2>
        <p class="section-desc">请按志愿顺序依次填报，建议遵循"冲-稳-保"原则</p>
      </div>

      <!-- 志愿填报提示 -->
      <div class="tip-box">
        <el-icon class="tip-icon"><Lightbulb /></el-icon>
        <div class="tip-content">
          <strong>填报建议：</strong>
          <p>1. 第一、二志愿可以填报稍高于自己分数的院校（冲）</p>
          <p>2. 第三、四志愿填报与自己分数相当的院校（稳）</p>
          <p>3. 第五、六志愿填报保底院校（保）</p>
        </div>
      </div>

      <div
        v-for="(item, index) in form.volunteers"
        :key="index"
        class="volunteer-item"
        :class="{ 'volunteer-empty': !item.school }"
      >
        <div class="volunteer-header">
          <span class="volunteer-number">第{{ index + 1 }}志愿</span>
          <span v-if="index === 0" class="volunteer-tag">第一志愿（最重要）</span>
        </div>
        
        <el-form label-width="80px">
          <el-row :gutter="20">
            <el-col :span="7">
              <el-form-item label="选择院校">
                <el-select 
                  v-model="item.school" 
                  placeholder="请选择院校"
                  :disabled="submitted"
                  filterable
                  remote
                  :remote-method="searchSchools"
                  loading-text="搜索中..."
                >
                  <el-option
                    v-for="school in schools"
                    :key="school.value"
                    :label="school.label"
                    :value="school.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="选择专业">
                <el-select 
                  v-model="item.major" 
                  placeholder="请选择专业"
                  :disabled="submitted || !item.school"
                >
                  <el-option
                    v-for="major in getMajorsBySchool(item.school)"
                    :key="major.value"
                    :label="major.label"
                    :value="major.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="5">
              <el-form-item label="是否服从调剂">
                <el-switch 
                  v-model="item.adjust" 
                  :disabled="submitted"
                  active-text="是"
                  inactive-text="否"
                ></el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <div class="volunteer-actions">
                <el-button
                  v-if="form.volunteers.length > 1"
                  type="danger"
                  icon="el-icon-delete"
                  @click="removeVolunteer(index)"
                  :disabled="submitted"
                  circle
                  size="small"
                ></el-button>
                <el-button
                  v-if="index > 0"
                  type="text"
                  icon="el-icon-arrow-up"
                  @click="moveUp(index)"
                  :disabled="submitted"
                  size="small"
                ></el-button>
                <el-button
                  v-if="index < form.volunteers.length - 1"
                  type="text"
                  icon="el-icon-arrow-down"
                  @click="moveDown(index)"
                  :disabled="submitted"
                  size="small"
                ></el-button>
              </div>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <div class="volunteer-add">
        <el-button
          type="primary"
          icon="el-icon-plus"
          @click="addVolunteer"
          :disabled="form.volunteers.length >= 6 || submitted"
        >
          添加志愿（当前{{ form.volunteers.length }}/6）
        </el-button>
      </div>

      <div class="step-actions">
        <el-button @click="prevStep">上一步</el-button>
        <el-button type="primary" @click="nextStep" :disabled="!validateStep3">
          下一步：确认信息
        </el-button>
      </div>
    </div>

    <!-- 步骤4：确认信息 -->
    <div v-if="currentStep === 3" class="form-section">
      <div class="section-header">
        <h2 class="section-title">第四步：确认填报信息</h2>
        <p class="section-desc">请仔细核对以下信息，确认无误后再提交</p>
      </div>

      <div class="review-card">
        <h3 class="review-title">考生信息</h3>
        <div class="review-row">
          <span class="review-label">姓名：</span>
          <span class="review-value">{{ form.name }}</span>
        </div>
        <div class="review-row">
          <span class="review-label">考生号：</span>
          <span class="review-value">{{ form.examineeNumber }}</span>
        </div>
        <div class="review-row">
          <span class="review-label">省份：</span>
          <span class="review-value">{{ getProvinceLabel(form.province) }}</span>
        </div>
        <div class="review-row">
          <span class="review-label">科类：</span>
          <span class="review-value">{{ getSubjectLabel(form.subjectType) }}</span>
        </div>
      </div>

      <div class="review-card">
        <h3 class="review-title">成绩信息</h3>
        <div class="review-row">
          <span class="review-label">高考总分：</span>
          <span class="review-value">{{ form.totalScore }}分</span>
        </div>
        <div class="review-row">
          <span class="review-label">省内排名：</span>
          <span class="review-value">第{{ form.ranking }}名</span>
        </div>
        <div class="review-row">
          <span class="review-label">报考批次：</span>
          <span class="review-value">{{ getBatchLabel(form.batch) }}</span>
        </div>
      </div>

      <div class="review-card">
        <h3 class="review-title">志愿填报（{{ form.volunteers.length }}个）</h3>
        <div 
          v-for="(item, index) in form.volunteers" 
          :key="index"
          class="review-volunteer"
        >
          <span class="volunteer-order">第{{ index + 1 }}志愿：</span>
          <span class="volunteer-school">{{ getSchoolLabel(item.school) }}</span>
          <span class="volunteer-major">— {{ getMajorLabel(item.school, item.major) }}</span>
          <span class="volunteer-adjust" :class="{ yes: item.adjust }">
            {{ item.adjust ? '（服从调剂）' : '（不服从调剂）' }}
          </span>
        </div>
      </div>

      <!-- 确认复选框 -->
      <div class="confirm-check">
        <el-checkbox v-model="confirmed" :disabled="submitted">
          我已仔细核对以上信息，确认填报无误
        </el-checkbox>
      </div>

      <div class="step-actions">
        <el-button @click="prevStep">上一步</el-button>
        <el-button 
          type="success" 
          icon="el-icon-check"
          @click="submitForm" 
          :disabled="!confirmed || submitted"
        >
          {{ submitted ? '已提交' : '模拟提交' }}
        </el-button>
      </div>
    </div>

    <!-- 提交成功提示 -->
    <div v-if="showSuccess" class="success-modal">
      <div class="success-content">
        <h2>填报成功！</h2>
        <p>您已完成模拟志愿填报，以下是您的填报信息：</p>
        <div class="success-summary">
          <p><strong>考生：</strong>{{ form.name }}</p>
          <p><strong>考生号：</strong>{{ form.examineeNumber }}</p>
          <p><strong>总分：</strong>{{ form.totalScore }}分</p>
          <p><strong>填报志愿数：</strong>{{ form.volunteers.length }}个</p>
        </div>
        <div class="success-actions">
          <el-button type="primary" @click="resetForm">重新模拟</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { Lightbulb, CheckCircle } from "@element-plus/icons-vue";
import { submitCollegeApplication } from "@/api/collegeApplicationApi";
import { getAllSchoolsWithMajors } from "@/api/schoolApi";
import { ElMessage } from "element-plus";
import {
  ElButton,
  ElForm,
  ElFormItem,
  ElInput,
  ElInputNumber,
  ElSelect,
  ElOption,
  ElRow,
  ElCol,
  ElSwitch,
  ElCheckbox,
  ElIcon,
} from "element-plus";

export default {
  name: "MockList",
  components: {
    ElButton,
    ElForm,
    ElFormItem,
    ElInput,
    ElInputNumber,
    ElSelect,
    ElOption,
    ElRow,
    ElCol,
    ElSwitch,
    ElCheckbox,
    ElIcon,
    Lightbulb,
    CheckCircle,
  },
  setup() {
    const currentStep = ref(0);
    const confirmed = ref(false);
    const submitted = ref(false);
    const showSuccess = ref(false);

    const progressSteps = [
      "填写考生信息",
      "填写成绩信息",
      "填报志愿",
      "确认提交",
    ];

    const progressPercent = computed(() => {
      return ((currentStep.value + 1) / progressSteps.length) * 100;
    });

    const provinces = [
      { value: "beijing", label: "北京市" },
      { value: "shanghai", label: "上海市" },
      { value: "tianjin", label: "天津市" },
      { value: "chongqing", label: "重庆市" },
      { value: "hebei", label: "河北省" },
      { value: "shanxi", label: "山西省" },
      { value: "inner-mongolia", label: "内蒙古自治区" },
      { value: "liaoning", label: "辽宁省" },
      { value: "jilin", label: "吉林省" },
      { value: "heilongjiang", label: "黑龙江省" },
      { value: "jiangsu", label: "江苏省" },
      { value: "zhejiang", label: "浙江省" },
      { value: "anhui", label: "安徽省" },
      { value: "fujian", label: "福建省" },
      { value: "jiangxi", label: "江西省" },
      { value: "shandong", label: "山东省" },
      { value: "henan", label: "河南省" },
      { value: "hubei", label: "湖北省" },
      { value: "hunan", label: "湖南省" },
      { value: "guangdong", label: "广东省" },
      { value: "guangxi", label: "广西壮族自治区" },
      { value: "hainan", label: "海南省" },
      { value: "sichuan", label: "四川省" },
      { value: "guizhou", label: "贵州省" },
      { value: "yunnan", label: "云南省" },
      { value: "tibet", label: "西藏自治区" },
      { value: "shaanxi", label: "陕西省" },
      { value: "gansu", label: "甘肃省" },
      { value: "qinghai", label: "青海省" },
      { value: "ningxia", label: "宁夏回族自治区" },
      { value: "xinjiang", label: "新疆维吾尔自治区" },
      { value: "hongkong", label: "香港特别行政区" },
      { value: "macao", label: "澳门特别行政区" },
      { value: "taiwan", label: "台湾省" },
    ];

    const schools = ref([]);
    const schoolMajors = ref({});

    const loadSchoolsWithMajors = async () => {
      try {
        const response = await getAllSchoolsWithMajors();
        if (response.data.code === 200) {
          const schoolList = response.data.data.list;
          schools.value = schoolList.map(school => ({
            value: school.id.toString(),
            label: school.name
          }));
          
          schoolList.forEach(school => {
            schoolMajors.value[school.id.toString()] = school.majors.map(major => ({
              value: major.id.toString(),
              label: major.name
            }));
          });
        } else {
          ElMessage.error("获取院校数据失败");
        }
      } catch (error) {
        console.error("获取院校数据失败:", error);
        ElMessage.error("获取院校数据失败，请稍后重试");
      }
    };

    const rules = {
      name: [
        { required: true, message: "请输入考生姓名", trigger: "blur" }
      ],
      examineeNumber: [
        { required: true, message: "请输入考生号（14位）", trigger: "blur" },
        { min: 14, max: 14, message: "考生号必须为14位", trigger: "blur" }
      ],
      province: [
        { required: true, message: "请选择省份", trigger: "change" }
      ],
      subjectType: [
        { required: true, message: "请选择科类", trigger: "change" }
      ],
      totalScore: [
        { required: true, message: "请输入高考总分", trigger: "blur" },
        { min: 0, max: 750, message: "高考总分应在0-750之间", trigger: "blur" }
      ],
      ranking: [
        { required: true, message: "请输入省内排名", trigger: "blur" },
        { min: 1, message: "排名必须大于0", trigger: "blur" }
      ],
      batch: [
        { required: true, message: "请选择批次", trigger: "change" }
      ]
    };

    const form = ref({
      name: "",
      examineeNumber: "",
      province: "",
      subjectType: "",
      totalScore: 600,
      ranking: 10000,
      batch: "first",
      volunteers: [
        { school: "", major: "", adjust: true },
      ],
    });

    const LOCAL_STORAGE_KEY = "mockApplicationData";

    const saveToLocalStorage = () => {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(form.value));
    };

    const loadFromLocalStorage = () => {
      const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          form.value = { ...form.value, ...parsed };
        } catch (error) {
          console.error("读取本地存储数据失败:", error);
        }
      }
    };

    watch(form, () => {
      saveToLocalStorage();
    }, { deep: true });

    onMounted(() => {
      loadFromLocalStorage();
      loadSchoolsWithMajors();
    });

    const validateStep1 = computed(() => {
      return form.value.name && 
             form.value.examineeNumber && form.value.examineeNumber.length === 14 && 
             form.value.province && 
             form.value.subjectType;
    });

    const validateStep2 = computed(() => {
      return form.value.totalScore > 0 && form.value.ranking > 0 && form.value.batch;
    });

    const validateStep3 = computed(() => {
      return form.value.volunteers.length > 0 && 
             form.value.volunteers.every(v => v.school && v.major);
    });

    const nextStep = () => {
      if (currentStep.value < progressSteps.length - 1) {
        saveToLocalStorage();
        currentStep.value++;
      }
    };

    const prevStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    };

    const addVolunteer = () => {
      if (form.value.volunteers.length < 6) {
        form.value.volunteers.push({
          school: "",
          major: "",
          adjust: true,
        });
      }
    };

    const removeVolunteer = (index) => {
      if (form.value.volunteers.length > 1) {
        form.value.volunteers.splice(index, 1);
      }
    };

    const moveUp = (index) => {
      if (index > 0) {
        const temp = form.value.volunteers[index];
        form.value.volunteers[index] = form.value.volunteers[index - 1];
        form.value.volunteers[index - 1] = temp;
      }
    };

    const moveDown = (index) => {
      if (index < form.value.volunteers.length - 1) {
        const temp = form.value.volunteers[index];
        form.value.volunteers[index] = form.value.volunteers[index + 1];
        form.value.volunteers[index + 1] = temp;
      }
    };

    const searchSchools = (query) => {
      console.log("搜索院校:", query);
    };

    const getMajorsBySchool = (school) => {
      return schoolMajors.value[school] || [];
    };

    const getProvinceLabel = (value) => {
      const province = provinces.find(p => p.value === value);
      return province ? province.label : value;
    };

    const getSubjectLabel = (value) => {
      const map = { science: "理科", arts: "文科", new: "新高考" };
      return map[value] || value;
    };

    const getBatchLabel = (value) => {
      const map = {
        early: "本科提前批",
        first: "本科第一批",
        second: "本科第二批",
        specialty: "专科批"
      };
      return map[value] || value;
    };

    const getSchoolLabel = (value) => {
      const school = schools.value.find(s => s.value === value);
      return school ? school.label : value;
    };

    const getMajorLabel = (school, major) => {
      const majors = schoolMajors.value[school] || [];
      const m = majors.find(m => m.value === major);
      return m ? m.label : major;
    };

    const submitForm = async () => {
      try {
        const submitData = {
          username: form.value.name,
          usernum: form.value.examineeNumber,
          province: getProvinceLabel(form.value.province),
          subject: form.value.subjectType === 'science' ? '理科' : '文科',
          totalscore: form.value.totalScore.toString(),
          provincialranking: form.value.ranking.toString(),
          batch: getBatchLabel(form.value.batch),
        };

        for (let i = 0; i < form.value.volunteers.length; i++) {
          const volunteer = form.value.volunteers[i];
          if (volunteer.school) {
            submitData[`school${i + 1}`] = getSchoolLabel(volunteer.school);
            submitData[`major${i + 1}`] = getMajorLabel(volunteer.school, volunteer.major) || "";
            submitData[`transfer${i + 1}`] = volunteer.adjust ? "0" : "1";
          }
        }

        const response = await submitCollegeApplication(submitData);
        
        if (response.data.code === 200) {
          ElMessage.success("提交成功！");
          submitted.value = true;
          showSuccess.value = true;
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        } else {
          ElMessage.error(response.data.msg || "提交失败");
        }
      } catch (error) {
        console.error("提交失败:", error);
        ElMessage.error("提交失败，请稍后重试");
      }
    };

    const resetForm = () => {
      currentStep.value = 0;
      confirmed.value = false;
      submitted.value = false;
      showSuccess.value = false;
      form.value = {
        name: "",
        examineeNumber: "",
        province: "",
        subjectType: "",
        totalScore: 600,
        ranking: 10000,
        batch: "first",
        volunteers: [
          { school: "", major: "", adjust: true },
        ],
      };
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    };

    return {
      currentStep,
      confirmed,
      submitted,
      showSuccess,
      progressSteps,
      progressPercent,
      provinces,
      schools,
      rules,
      form,
      validateStep1,
      validateStep2,
      validateStep3,
      nextStep,
      prevStep,
      addVolunteer,
      removeVolunteer,
      moveUp,
      moveDown,
      searchSchools,
      getMajorsBySchool,
      getProvinceLabel,
      getSubjectLabel,
      getBatchLabel,
      getSchoolLabel,
      getMajorLabel,
      submitForm,
      resetForm,
    };
  },
};
</script>

<style scoped>
.container {
  max-width: 1440px;
  width: calc(100% - 20px);
  margin: 60px auto;
  padding: 10px;
  box-sizing: border-box;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.main-title {
  font-size: 28px;
  color: #303133;
  margin: 10px 0px;
  margin-bottom: 10px;
}

.subtitle {
  font-size: 14px;
  color: #909399;
}

.progress-section {
  margin-bottom: 30px;
}

.progress-bar {
  height: 6px;
  background-color: #ebeef5;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 20px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #409eff, #67c23a);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #ebeef5;
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step-text {
  font-size: 12px;
  color: #909399;
}

.step-item.active .step-number {
  background-color: #409eff;
  color: white;
}

.step-item.active .step-text {
  color: #409eff;
}

.step-item.completed .step-number {
  background-color: #67c23a;
  color: white;
}

.step-item.completed .step-text {
  color: #67c23a;
}

.form-section {
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 25px;
}

.section-title {
  font-size: 18px;
  color: #303133;
  margin-bottom: 5px;
}

.section-desc {
  font-size: 14px;
  color: #909399;
}

.info-form {
  margin-bottom: 25px;
}

.tip-box {
  background: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 25px;
  display: flex;
  gap: 12px;
}

.tip-icon {
  font-size: 24px;
  color: #409eff;
  flex-shrink: 0;
}

.tip-content {
  font-size: 14px;
  color: #606266;
}

.tip-content strong {
  color: #409eff;
}

.tip-content p {
  margin: 5px 0;
}

.volunteer-item {
  margin-bottom: 20px;
  padding: 20px;
  border: 2px dashed #ebeef5;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.volunteer-item:hover {
  border-color: #c0c4cc;
}

.volunteer-empty {
  opacity: 0.6;
}

.volunteer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.volunteer-number {
  font-weight: bold;
  color: #303133;
}

.volunteer-tag {
  font-size: 12px;
  color: #e6a23c;
  background: #fdf6ec;
  padding: 2px 8px;
  border-radius: 4px;
}

.volunteer-actions {
  display: flex;
  gap: 5px;
  justify-content: flex-end;
}

.volunteer-add {
  margin-bottom: 25px;
}

.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.review-card {
  background: #fafafa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.review-title {
  font-size: 16px;
  color: #303133;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.review-row {
  display: flex;
  margin-bottom: 10px;
}

.review-label {
  width: 100px;
  color: #909399;
  font-size: 14px;
}

.review-value {
  color: #303133;
  font-size: 14px;
}

.review-volunteer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px dashed #ebeef5;
}

.review-volunteer:last-child {
  border-bottom: none;
}

.volunteer-order {
  font-weight: 500;
  color: #303133;
}

.volunteer-school {
  color: #409eff;
}

.volunteer-major {
  color: #67c23a;
}

.volunteer-adjust {
  font-size: 12px;
  color: #909399;
}

.volunteer-adjust.yes {
  color: #67c23a;
}

.confirm-check {
  margin: 25px 0;
  padding-left: 20px;
}

.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.success-content {
  background: white;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  max-width: 450px;
  width: 90%;
}

.success-content h2 {
  font-size: 24px;
  color: #303133;
  margin: 0 0 15px;
}

.success-content p {
  color: #909399;
  margin-bottom: 20px;
}

.success-summary {
  background: #fafafa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
  text-align: left;
}

.success-summary p {
  margin: 8px 0;
  color: #606266;
}

.success-actions {
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .container {
    padding: 15px;
    margin-top: 70px;
  }
  
  .step-text {
    font-size: 10px;
  }
  
  .volunteer-actions {
    flex-wrap: wrap;
  }
}
</style>